import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  Truck,
  Edit,
  Trash2,
  MoreVertical,
  Search,
  Download,
  BarChart3,
  FileText,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import { all_routes } from "../../Router/all_routes";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ReactSelect } from "@/components/ui/react-select";
import { Country, State } from "country-state-city";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer } from "@/components/layout/PageContainer";
import SupplierExpensesReportModal from "../../components/modals/SupplierExpensesReportModal";

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const Suppliers = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 500);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const currentStore = useSelector((state) => state.current_store);
  const [selectedStoreId, setSelectedStoreId] = useState(
    currentStore?.id ? String(currentStore.id) : "",
  );
  const [reportModalSupplier, setReportModalSupplier] = useState(null);


  const [downloadingSupplierId, setDownloadingSupplierId] = useState(null);

  useEffect(() => {
    if (currentStore?.id) {
      setSelectedStoreId(String(currentStore.id));
    }
  }, [currentStore?.id]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, selectedStoreId]);

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/store`);
      return res.data.data || res.data || [];
    },
  });

  const handleViewSupplierReport = (sup) => {
    setReportModalSupplier(sup);


  };

  const handleDownloadSupplierPdf = async (sup) => {
    try {
      setDownloadingSupplierId(sup.id);
      const name = sup.company_name || sup.name || "Supplier";
      toast.info(`Generating expense report PDF for ${name}...`);
      const params = {
        supplier_id: String(sup.id),
      };
      if (selectedStoreId && selectedStoreId !== "all") {
        params.store_id = selectedStoreId;
      }
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SUPPLIERWISE_EXPENSES,
        {
          params,
          responseType: "blob",
        },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `supplier-expense-report-${sup.id}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Expense report PDF downloaded successfully!");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to download expense report PDF",
      );
    } finally {
      setDownloadingSupplierId(null);
    }
  };

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    status: "active",
  });

  const countries = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    [],
  );
  const selectedCountryCode = useMemo(() => {
    return (
      Country.getAllCountries().find((c) => c.name === formData.country)
        ?.isoCode || ""
    );
  }, [formData.country]);
  const states = useMemo(() => {
    return selectedCountryCode
      ? State.getStatesOfCountry(selectedCountryCode).map((s) => ({
        value: s.isoCode,
        label: s.name,
      }))
      : [];
  }, [selectedCountryCode]);

  // Mutations
  const { data: supplierData, isLoading: isLoadingSuppliers } = useQuery({
    queryKey: [
      "suppliers",
      currentPage,
      pageSize,
      debouncedSearchText,
      selectedStoreId,
    ],
    enabled: hasPermission("supplier read"),
    queryFn: async () => {
      try {
        const params = { page: currentPage, perPage: pageSize };
        if (debouncedSearchText) {
          params.search = debouncedSearchText;
        }
        if (selectedStoreId && selectedStoreId !== "all") {
          params.store_id = selectedStoreId;
        }
        const response = await apiClient.get(`/supplier`, { params });
        return response.data;
      } catch (error) {
        toast.error(
          `Failed to load suppliers: ${error.response?.data?.message || error.message}`,
        );
        throw error;
      }
    },
  });

  const suppliers = supplierData?.data || [];
  const pagination = {
    current_page:
      supplierData?.meta?.current_page || supplierData?.current_page || 1,
    last_page: supplierData?.meta?.last_page || supplierData?.last_page || 1,
    per_page: supplierData?.meta?.per_page || supplierData?.per_page || 10,
    total: supplierData?.meta?.total || supplierData?.total || suppliers.length,
    from: supplierData?.meta?.from || supplierData?.from || 0,
    to: supplierData?.meta?.to || supplierData?.to || 0,
  };

  // Mutations
  const saveSupplierMutation = useMutation({
    mutationFn: async (data) => {
      const url = data.id ? `/supplier/${data.id}` : `/supplier`;
      const method = data.id ? "put" : "post";
      return await apiClient({ method, url, data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success(`Supplier ${isEdit ? "updated" : "added"} successfully`);
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong");
    },
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(`/supplier/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Supplier deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong");
    },
  });

  useEffect(() => {
    if (!hasPermission("supplier read")) {
      toast.error("Unauthorized", {
        description:
          "You do not have permission to view this page. Redirecting to dashboard...",
      });
      setTimeout(() => {
        navigate(all_routes.dashboard);
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is not valid";
    }

    if (!formData.phone?.trim()) {
      errors.phone = "Phone is required";
    }

    if (!formData.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });

    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: null });
    }
  };

  const openAddModal = () => {
    setIsEdit(false);
    setFormData({
      id: null,
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      country: "",
      zip: "",
      status: "active",
    });
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (supplier) => {
    setIsEdit(true);
    setFormData({ ...supplier });
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      id: null,
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      country: "",
      zip: "",
      status: "active",
    });
    setValidationErrors({});
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors in the form before submitting");
      return;
    }
    saveSupplierMutation.mutate(formData);
  };

  // Server-side filtering is applied via query parameters
  const filteredData = suppliers;

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteSupplierMutation.mutate(deleteId);
    setIsDeleteDialogOpen(false);
  };

  const getAddressString = (supplier) => {
    const parts = [
      supplier.address,
      supplier.city,
      supplier.zip,
      supplier.state,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => handleViewSupplierReport(row.original)}
          className="font-medium text-left text-primary hover:underline cursor-pointer"
          title="Click to view supplier expense report"
        >
          {row.original.name || row.original.company_name || "-"}
        </button>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phone",
      header: "Phone",
      cell: ({ row }) => (
        <span>
          {row.original.phone !== null ? "+" + row.original.phone : "-"}
        </span>
      ),
    },
    {
      accessorKey: "address",
      header: "Address",
      cell: ({ row }) => getAddressString(row.original) || "-",
    },
    {
      accessorKey: "country",
      header: "Country",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const text = row.original.status || "active";
        return (
          <Badge variant={text === "active" ? "success" : "destructive"}>
            {text[0].toUpperCase() + text.slice(1)}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasPermission("report supplierwise expense view") && (
              <>
                <DropdownMenuItem
                  onClick={() => handleViewSupplierReport(row.original)}
                >
                  <BarChart3 className="mr-2 h-4 w-4" /> Expense Report
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDownloadSupplierPdf(row.original)}
                  disabled={downloadingSupplierId === row.original.id}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {downloadingSupplierId === row.original.id
                    ? "Downloading PDF..."
                    : "Download Expense PDF"}
                </DropdownMenuItem>
              </>
            )}
            {hasPermission("supplier update") && (
              <DropdownMenuItem onClick={() => openEditModal(row.original)}>
                <Edit className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
            )}
            {hasPermission("supplier delete") && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => handleDelete(row.original.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Supplier List"
          subtitle="Manage Your Suppliers"
        />
        <PageHeaderActions>
          {hasPermission("supplier create") && (
            <Button onClick={openAddModal}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Supplier
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search suppliers..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="w-full md:w-64">
          <ReactSelect
            options={[
              { value: "", label: "All Stores" },
              ...stores.map((s) => ({ value: String(s.id), label: s.name })),
            ]}
            value={
              selectedStoreId
                ? {
                  value: String(selectedStoreId),
                  label:
                    stores.find(
                      (s) => String(s.id) === String(selectedStoreId),
                    )?.name || "Selected Store",
                }
                : { value: "", label: "All Stores" }
            }
            onChange={(opt) => {
              setSelectedStoreId(opt?.value || "");
              setCurrentPage(1);
            }}
            placeholder="Filter by Store"
            isClearable
          />
        </div>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={filteredData}
          enableRowSelection={true}
          isLoading={isLoadingSuppliers}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize: pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            } else {
              setCurrentPage(updater.pageIndex + 1);
              setPageSize(updater.pageSize);
            }
          }}
          pageCount={pagination.last_page}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Suppliers: {pagination.total}
            </span>
          }
        />
      </TableCard>

      <SupplierExpensesReportModal
        open={Boolean(reportModalSupplier)}
        onOpenChange={(open) => !open && setReportModalSupplier(null)}
        supplier={reportModalSupplier}
        storeId={selectedStoreId}
      />

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <Truck size={20} />
              {isEdit ? "Edit" : "Add New"} Supplier
            </DialogTitle>
          </DialogHeader>
          <div className="px-5">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
              </TabsList>
              <div className="py-4">
                <TabsContent value="general" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        placeholder="Enter supplier name"
                        value={formData.name}
                        onChange={(e) =>
                          handleFormChange("name", e.target.value)
                        }
                        className={
                          validationErrors.name ? "border-destructive" : ""
                        }
                      />
                      {validationErrors.name && (
                        <p className="text-destructive text-sm">
                          {validationErrors.name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter email address"
                        value={formData.email}
                        onChange={(e) =>
                          handleFormChange("email", e.target.value)
                        }
                        className={
                          validationErrors.email ? "border-destructive" : ""
                        }
                      />
                      {validationErrors.email && (
                        <p className="text-destructive text-sm">
                          {validationErrors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        Phone <span className="text-destructive">*</span>
                      </Label>
                      <PhoneInput
                        country={"au"}
                        value={formData.phone}
                        onChange={(phone) => handleFormChange("phone", phone)}
                        inputClass={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus:outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50 ${validationErrors.phone ? "border-destructive" : ""}`}
                        containerClass="w-100"
                        placeholder="Enter phone number"
                      />
                      {validationErrors.phone && (
                        <p className="text-destructive text-sm">
                          {validationErrors.phone}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="status">
                        Status <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        options={[
                          { value: "active", label: "Active" },
                          { value: "inactive", label: "Inactive" },
                        ]}
                        value={[
                          { value: "active", label: "Active" },
                          { value: "inactive", label: "Inactive" },
                        ].find((opt) => opt.value === formData.status)}
                        onChange={(option) =>
                          handleFormChange("status", option ? option.value : "")
                        }
                        placeholder="Select Status"
                      />
                      {validationErrors.status && (
                        <p className="text-destructive text-sm">
                          {validationErrors.status}
                        </p>
                      )}
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="address" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address">Street No. & Address</Label>
                      <AddressAutocomplete
                        id="address"
                        placeholder="Search for address…"
                        value={formData.address}
                        onChange={(val) => handleFormChange("address", val)}
                        onPlaceSelect={({
                          address,
                          city,
                          state,
                          country,
                          postcode,
                        }) => {
                          setFormData((prev) => ({
                            ...prev,
                            ...(address && { address }),
                            ...(city && { city }),
                            ...(state && { state }),
                            ...(country && { country }),
                            ...(postcode && { zip: postcode }),
                          }));
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        placeholder="Enter city"
                        value={formData.city}
                        onChange={(e) =>
                          handleFormChange("city", e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP Code</Label>
                      <Input
                        id="zip"
                        placeholder="Enter ZIP code"
                        value={formData.zip}
                        onChange={(e) =>
                          handleFormChange("zip", e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <ReactSelect
                        options={countries}
                        value={countries.find(
                          (c) => c.label === formData.country,
                        )}
                        onChange={(opt) =>
                          handleFormChange("country", opt ? opt.label : "")
                        }
                        placeholder="Select country"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <ReactSelect
                        options={states}
                        value={states.find((s) => s.label === formData.state)}
                        onChange={(opt) =>
                          handleFormChange("state", opt ? opt.label : "")
                        }
                        placeholder="Select state"
                        isDisabled={!formData.country}
                      />
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
          <DialogFooter className="justify-start border-t p-5">
            <Button
              onClick={handleSave}
              disabled={saveSupplierMutation.isPending}
            >
              {saveSupplierMutation.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              supplier and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default Suppliers;
